import React, { useEffect, useState } from 'react'
import { ComboboxItem, Group, Select, SelectProps } from '@mantine/core'
import CenterService from '../../services/centers/center.service.js'
import UsersService from '../../services/users/users.service.js'
import CompanyService from '../../services/companies/companies.service.js'
import CenterSettingsService from '../../services/settings/centers/centerSettings.service.js'
import AccountingEntryService from '../../services/accounting-entry/accountingEntry.service.js'
import { SuppliersService } from '../../services/purchase-fuel/suppliers/suppliers.service.js'
import { OwnFuelStationsService } from '../../services/own-fuel-stations/ownFuelStations.service.js'
import AppIcon from '../common/AppIcon.tsx'
import { IconCheck } from '@tabler/icons-react'
import { useTranslation } from 'react-i18next'
import { collections } from '../../config/app.ts'
import AppInputWrapper from './AppInputWrapper.tsx'
import { useDebouncedCallback } from '@mantine/hooks'

interface CustomProps extends SelectProps {
  icon?: string
  clearable?: boolean
  entity?:
    | 'center'
    | 'centerType'
    | 'company'
    | 'month'
    | 'year'
    | 'weather'
    | 'salesType'
  name?: string
  value: string | null
  label?: string
  labelh?: string
  placeholder?: string
  w?: string | number
  options?: []
  onChange?: any
  debounced?: boolean
  local?: boolean
}

const EntityDropdown = ({
  icon,
  clearable = true,
  entity,
  name,
  value,
  label,
  labelh,
  placeholder = label,
  w = '100%',
  options = [],
  onChange = () => false,
  debounced = true,
  local = true,
  ...otherProps
}: CustomProps) => {
  const [internalOptions, setInternalOptions] = useState<
    { value: string; label: string }[] | undefined
  >([])
  const { t, i18n } = useTranslation()

  const [localValue, setLocalValue] = useState<string | null>(value || null)

  useEffect(() => {
    setLocalValue(value || null)
  }, [value])

  const debouncedOnChange = useDebouncedCallback(
    (v: string | null, o: ComboboxItem) => {
      onChange?.(
        name ? { target: { name, value: v } } : { value: v, option: o },
      )
    },
    300, // milisegundos de debounce
  )

  useEffect(() => {
    if (options?.length === 0 && internalOptions?.length === 0) fillOptions()
  }, [])
  useEffect(() => {
    if (options?.length > 0 && internalOptions?.length === 0) {
      fillOptions()
    }
  }, [options])

  const fillOptions = async () => {
    if (entity === 'center') {
      const data = await CenterService.getAll({ sort: 'name' })
      setInternalOptions(
        data?.resp?.map((item: any) => ({
          value: item?._id,
          label: item?.basicData?.name,
        })),
      )
    } else if (entity === 'company') {
      const data = await CompanyService.getAll({ sort: 'name' })
      setInternalOptions(
        data?.resp?.map((item: any) => ({
          value: item?._id,
          label: item?.name,
        })),
      )
    } else if (entity === 'accountingEntryCompany') {
      const data = await AccountingEntryService.getCompanies({
        sort: 'companyName',
      })
      setInternalOptions(
        data?.resp?.map((item: any) => ({
          value: String(item?.companyCode),
          label: item?.companyName,
        })),
      )
    } else if (entity === 'ownFuelStation') {
      const data = await OwnFuelStationsService.getAll({ sort: 'centerName' })
      setInternalOptions(
        data?.resp?.map((item: any) => ({
          value: item?._id,
          label: item?.centerName,
        })),
      )
    } else if (entity === 'soap') {
      const data = await CenterSettingsService?.soapTypes?.getAll({})
      setInternalOptions(
        data?.resp?.map((item: any) => ({
          value: item?._id,
          label: item?.name,
        })),
      )
    } else if (entity === 'gas') {
      const data = await CenterSettingsService?.gasTypes?.getAll({})
      setInternalOptions(
        data?.resp?.map((item: any) => ({
          value: item?._id,
          label: item?.name,
        })),
      )
    } else if (entity === 'heater') {
      const data = await CenterSettingsService?.heaterTypes?.getAll({})
      setInternalOptions(
        data?.resp?.map((item: any) => ({
          value: item?._id,
          label: item?.name,
        })),
      )
    } else if (entity === 'supervisor') {
      const data = await UsersService.getAll({ sort: 'name' })
      setInternalOptions(
        data?.resp?.map((item: any) => ({
          value: item?._id,
          label: item?.name,
        })),
      )
    } else if (entity === 'centerType') {
      setInternalOptions(
        collections?.centerTypes?.map((item: any) => ({
          value: item?._id,
          label: t(item?.value),
        })),
      )
    } else if (entity === 'activityType') {
      setInternalOptions(
        collections?.activityTypes?.map((item: any) => ({
          value: item?._id,
          label: t(item?.value),
        })),
      )
    } else if (entity === 'zone') {
      setInternalOptions(
        collections?.zones?.map((item: any) => ({ value: item, label: item })),
      )
    } else if (entity === 'technics') {
      setInternalOptions(
        collections?.technics?.map((item: any) => ({
          value: item?._id,
          label: t(item?.value),
        })),
      )
    } else if (entity === 'supplier') {
      const data = await SuppliersService.getAll({ sort: 'name' })
      const rows = data?.resp?.map((item) => {
        return { value: item?._id, label: item?.name }
      })
      rows.unshift({
        value: 'all',
        label: t('all').toUpperCase().concat(' ***'),
      })
      rows.unshift({ value: '', label: '...' })
      setInternalOptions(rows)
    } else if (entity === 'month') {
      setInternalOptions([
        { value: '0', label: 'Enero' },
        { value: '1', label: 'Febrero' },
        { value: '2', label: 'Marzo' },
        { value: '3', label: 'Abril' },
        { value: '4', label: 'Mayo' },
        { value: '5', label: 'Junio' },
        { value: '6', label: 'Julio' },
        { value: '7', label: 'Agosto' },
        { value: '8', label: 'Septiembre' },
        { value: '9', label: 'Octubre' },
        { value: '10', label: 'Noviembre' },
        { value: '11', label: 'Diciembre' },
      ])
    } else if (entity === 'year') {
      setInternalOptions(
        Array.from({ length: 11 }, (_, i) => {
          const year = new Date().getFullYear() - 5 + i
          return { value: String(year), label: year.toString() }
        }),
      )
    } else if (entity === 'weather') {
      setInternalOptions(
        Object.keys(collections?.weather || {}).map((key) => ({
          value: key,
          label: t(key),
        })),
      )
    } else {
      setInternalOptions(
        options?.map((item: any) => ({
          value: item?._id || item?.value,
          label: item?.name || item?.label,
        })),
      )
    }
  }

  const EntityIcon = ({ value }) => {
    if (icon) return <AppIcon icon={icon} />
    if (entity === 'center' || entity === 'centerType')
      return <AppIcon icon="centerNew" />
    if (entity === 'weather')
      return (
        <AppIcon
          icon={collections?.weather?.[`${value}`]?.icon || 'weather'}
          color={collections?.weather?.[`${value}`]?.color || undefined}
        />
      )
    return <AppIcon icon={entity} />
    // if(entity === "activityType") return <AppIcon icon="activityType" />
    // if(entity === "supervisor") return <AppIcon icon="supervisor" />
    // if(entity === "zone") return <AppIcon icon="zone" />
    // if(entity === "month") return <AppIcon icon="calendar" />
    // if(entity === "year") return <AppIcon icon="calendar" />

    // if(entity === "salesType") return <AppIcon icon="salesType" />
    // if(entity === "expense") return <AppIcon icon="expense" />
    // return <AppIcon icon="question" />
  }

  const renderSelectOption: SelectProps['renderOption'] = ({
    option,
    checked,
  }) => (
    <Group flex="1" gap="xs" c={checked ? 'moure-orange' : 'text'}>
      <EntityIcon value={option?.value} />
      {option.label}
      {checked && <IconCheck size={14} style={{ marginInlineStart: 'auto' }} />}
    </Group>
  )

  return (
    <AppInputWrapper w={w} label={label}>
      {labelh && t(labelh)}
      <Select
        {...otherProps}
        clearable={clearable}
        w={w}
        comboboxProps={{
          offset: 0,
          position: 'bottom',
          middlewares: { flip: false, shift: false },
          shadow: 'sm',
        }}
        label={label && t(label)}
        placeholder={(label && t(label)) || (placeholder && t(placeholder))}
        leftSection={<EntityIcon value={localValue} />}
        // searchable
        data={internalOptions}
        renderOption={renderSelectOption}
        value={local ? localValue : value}
        onChange={(_value, option) => {
          if (local) setLocalValue(_value)
          if (onChange) {
            if (debounced) debouncedOnChange(_value, option)
            else onChange(_value, option)
          }
        }}
      />
    </AppInputWrapper>
  )
}

export default EntityDropdown
